import { useAuth, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface ObservationRelation {
  id: string
  content_type: string  // 'symptom' | 'activity'
  object_id: string
  content_object: {
    id: string
    symptom_type?: string
    anatomy?: string
    severity?: string
    occurred_at?: string
    activity_type?: string
    date?: string
  }
  relation_type: 'related_to' | 'context_for' | 'follows' | 'contradicts' | 'confirms'
  strength: number
  discovery_method: 'agent' | 'manual' | 'llm_analysis' | 'time_proximity'
  created_at: string
}

export interface Observation {
  id: string
  text: string
  thread_id?: string
  message_id?: string
  source_message_text?: string
  relations: ObservationRelation[]
  created_at: string
  updated_at: string
}

export interface ObservationFilters {
  limit?: number
  search?: string
  thread_id?: string
  symptom_id?: string  // Filter observations related to symptom
  activity_id?: string  // Filter observations related to activity
}

export interface CreateObservationData {
  text: string
  related_symptom_ids?: string[]
  related_activity_ids?: string[]
  relation_type?: string
}

export interface UpdateObservationData {
  observation_id: string
  text?: string
  related_symptom_ids?: string[]
  related_activity_ids?: string[]
}

export function useObservations(filters: ObservationFilters = {}) {
  const { token } = useAuth()

  const queryKey = ['observations', filters]

  const observationsQuery = useQuery({
    queryKey,
    queryFn: async (): Promise<Observation[]> => {
      if (!token) throw new Error('Not authenticated')

      const params = new URLSearchParams()
      if (filters.search) params.append('search', filters.search)
      if (filters.thread_id) params.append('thread_id', filters.thread_id)
      if (filters.symptom_id) params.append('symptom_id', filters.symptom_id)
      if (filters.activity_id) params.append('activity_id', filters.activity_id)
      if (filters.limit) params.append('limit', filters.limit.toString())

      const url = `/api/observations/${params.toString() ? '?' + params.toString() : ''}`

      const response = await authorizedFetch(url)

      if (!response.ok) {
        throw new Error('Failed to fetch observations')
      }

      return response.json()
    },
    enabled: !!token,
    staleTime: 30 * 1000, // 30 seconds
  })

  return {
    observations: observationsQuery.data || [],
    isLoading: observationsQuery.isLoading,
    error: observationsQuery.error,
    refetch: observationsQuery.refetch,
  }
}

export function useObservation(observationId: string | null) {
  const { token } = useAuth()

  const observationQuery = useQuery({
    queryKey: ['observations', observationId],
    queryFn: async (): Promise<Observation> => {
      if (!token || !observationId) throw new Error('Not authenticated or no observation ID')

      const response = await authorizedFetch(`/api/observations/${observationId}`)

      if (!response.ok) {
        throw new Error('Failed to fetch observation')
      }

      return response.json()
    },
    enabled: !!token && !!observationId,
  })

  return {
    observation: observationQuery.data,
    isLoading: observationQuery.isLoading,
    error: observationQuery.error,
  }
}

export function useCreateObservation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateObservationData): Promise<Observation> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch('/api/observations/', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to create observation')
      }

      return response.json()
    },
    onSuccess: () => {
      // Invalidate observations list cache
      queryClient.invalidateQueries({ queryKey: ['observations'] })
    },
  })
}

export function useUpdateObservation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateObservationData): Promise<Observation> => {
      if (!token) throw new Error('Not authenticated')

      const { observation_id, ...updateData } = data

      const response = await authorizedFetch(`/api/observations/${observation_id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to update observation')
      }

      return response.json()
    },
    onSuccess: (data) => {
      // Invalidate observations list and specific observation cache
      queryClient.invalidateQueries({ queryKey: ['observations'] })
      queryClient.invalidateQueries({ queryKey: ['observations', data.id] })
    },
  })
}

export function useDeleteObservation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (observationId: string): Promise<void> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/observations/${observationId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to delete observation')
      }
    },
    onSuccess: () => {
      // Invalidate observations list cache
      queryClient.invalidateQueries({ queryKey: ['observations'] })
    },
  })
}

